import {
  PatientAnswer,
  PracticeFormQuestion,
  PracticeTherapyDepartment,
  PracticeTreatmentContractType,
} from "@physiofit-digital/physiofit_types";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useConfirmation } from "../../../core/components/confirmationModal/useEventIndicator";
import { useEventIndicator } from "../../../core/components/eventIndicator/useEventIndicator";
import {
  getCompletedFormWrites,
  getCurrentCostBearerScreenStep,
  getCurrentFormIsLastPracticeForm,
  getCurrentPracticeFormIndex,
  getCurrentPracticePatientCostBearerDetails,
  getCurrentPracticeTreatmentContracts,
  getIsReleaseSession,
  getPracticeFormId,
  getPracticePatientId,
  getRunStartFormIndex,
  getWebAppChoices,
} from "../../../core/store/currentFormSlice";
import { useMainStore } from "../../../core/store/mainStore";
import { useHandleParamNavigation } from "../../../core/utils/useHandleParamNavigation";
import { handleLogout } from "../../navigation/utils/handleLogout";
import { useEndReleaseSession } from "../../navigation/utils/webAppSession";
import { hasTreatmentContractDocument } from "./costBearerContractUtils";
import { handleDocumentAndRecipeUpload } from "./handleDocumentAndRecipeUpload";
import { uploadForm } from "./uploadForm";
import { useContractHandling } from "./useContractHandling";
import { useOtherPersonQuestion } from "./useOtherPersonQuestion";
import { usePersonalInfoHandling } from "./usePersonalInfoHandling";

const signatureWriteKey = (questionId: string) => `signature:${questionId}`;
const assetWriteKey = (assetUrl: string) => `asset:${assetUrl}`;
const FORM_SUBMITTED_KEY = "submission";

interface UseFormNavigationProps {
  currentQuestion: PracticeFormQuestion;
  currentQuestionIndex: number;
  setCurrentQuestionIndex: (index: number) => void;
  currentQuestionIsCostBearer: boolean;
  questionIsConditionAcceptance: boolean;
  questionIsPersonalInfo: boolean;
  questionIsContract: boolean;
  questionIsDocumentUpload: boolean;
  questionIsRecipeUpload: boolean;
  userAnswer: PatientAnswer;
  nextButtonDisabled: boolean;
  setNextButtonLoading: (loading: boolean) => void;
  amountTotalQuestions: number;
}

export const useFormNavigation = ({
  currentQuestion,
  currentQuestionIndex,
  setCurrentQuestionIndex,
  currentQuestionIsCostBearer,
  questionIsConditionAcceptance,
  questionIsPersonalInfo,
  questionIsContract,
  questionIsDocumentUpload,
  questionIsRecipeUpload,
  userAnswer,
  nextButtonDisabled,
  setNextButtonLoading,
  amountTotalQuestions,
}: UseFormNavigationProps) => {
  const navigate = useHandleParamNavigation();
  // useHandleParamNavigation cannot carry router state.
  const navigateWithState = useNavigate();
  const { t } = useTranslation();
  const eventIndicator = useEventIndicator();
  const confirm = useConfirmation();
  const { handleSignNormalContract, handleSignCostBearerContract } =
    useContractHandling();
  const { savePersonalInfo } = usePersonalInfoHandling();
  const { confirmSamePerson } = useOtherPersonQuestion();
  const leaveRecord = useMainStore((state) => state.leaveRecord);
  const rememberWebAppChoice = useMainStore((state) => state.rememberWebAppChoice);
  const endReleaseSession = useEndReleaseSession();

  const currentCostBearerScreenStep =
    useMainStore(getCurrentCostBearerScreenStep) ?? 0;
  const setCurrentCostBearerScreenStep = useMainStore(
    (state) => state.setCurrentCostBearerScreenStep,
  );
  const currentPracticeTreatmentContracts = useMainStore(
    getCurrentPracticeTreatmentContracts,
  );
  const currentPatientCostBearerDetails = useMainStore(
    getCurrentPracticePatientCostBearerDetails,
  );
  const setUserAnswerToQuestion = useMainStore(
    (state) => state.setUserAnswerToQuestion,
  );
  const completedWrites = useMainStore(getCompletedFormWrites);
  const recordCompletedWrite = useMainStore(
    (state) => state.recordCompletedFormWrite,
  );
  const rememberSubmittedForm = useMainStore(
    (state) => state.rememberSubmittedForm,
  );
  const setCurrentPracticePatientForm = useMainStore(
    (state) => state.setCurrentPracticePatientForm,
  );
  const setCurrentPracticeFormIndex = useMainStore(
    (state) => state.setCurrentPracticeFormIndex,
  );
  const isAdvancing = useRef(false);

  const selectedCostBearerContract =
    currentPracticeTreatmentContracts?.practiceTreatmentContracts?.[
      currentPatientCostBearerDetails?.departmentId as PracticeTherapyDepartment
    ]?.contracts?.[
      currentPatientCostBearerDetails?.costBearerId as PracticeTreatmentContractType
    ];
  const selectedCostBearerHasDocument = hasTreatmentContractDocument(
    selectedCostBearerContract,
  );

  const saveCostBearerAnswer = () => {
    if (!currentPatientCostBearerDetails) {
      console.error("Cost bearer details are missing");
      return;
    }

    setUserAnswerToQuestion(
      currentQuestion.id,
      {
        id: currentQuestion.answer.answerId,
        costBearerInfo: currentPatientCostBearerDetails,
      },
      currentQuestion.answer.answerId,
    );
  };

  const finishForm = () => {
    const state = useMainStore.getState();
    // After the link's last form the session ends on the success screen, outside InAppLayout, whose
    // isLoggedIn redirect would otherwise race the logout.
    if (getCurrentFormIsLastPracticeForm(state)) {
      navigateWithState("/welcome/success", { replace: true, state: { allFormsCompleted: true } });
      return;
    }
    // The submitted form would stay on screen until the next one installs, and "Weiter" on it would skip a form.
    setCurrentPracticePatientForm(undefined);
    setCurrentPracticeFormIndex((getCurrentPracticeFormIndex(state) ?? 0) + 1);
  };

  const moveToNextQuestion = async () => {
    if (currentQuestionIndex < amountTotalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      return;
    }

    // A second submission creates a second patientForms document rather than
    // updating the first.
    if (completedWrites[FORM_SUBMITTED_KEY]) {
      finishForm();
      return;
    }

    const success = await uploadForm();
    if (!success) {
      eventIndicator("error", t("ERROR_SAVING_ANSWERS"));
      return;
    }
    recordCompletedWrite(FORM_SUBMITTED_KEY);
    const state = useMainStore.getState();
    const practicePatientId = getPracticePatientId(state);
    const practiceFormId = getPracticeFormId(state);
    if (practicePatientId && practiceFormId) {
      rememberSubmittedForm(practicePatientId, practiceFormId);
    }
    finishForm();
  };

  const submitCurrentQuestion = async (isSkip?: boolean) => {
    if (questionIsConditionAcceptance) {
      if (currentQuestion) {
        setUserAnswerToQuestion(
          currentQuestion.id,
          { id: currentQuestion.answer.answerId, conditionAcceptedValue: true },
          currentQuestion.answer.answerId,
        );
      }
    }

    if (questionIsPersonalInfo) {
      const typed = userAnswer?.personalInfoDetails;
      if (!typed) {
        return;
      }
      // A release session (PF-6133) saves a name change without the question: staff chose the record and the
      // birthday matched, so a difference is a correction. False otherwise: the patient closed the question or left
      // this record for another person; nothing is saved here.
      if (!getIsReleaseSession(useMainStore.getState()) && !(await confirmSamePerson(typed))) {
        return;
      }
      // Stay on the question so the patient can retry. Advancing would carry
      // them on with nothing saved (PF-5637).
      const saved = await savePersonalInfo(typed);
      if (!saved) {
        eventIndicator("error", t("ERROR_SAVING_ANSWERS"));
        return;
      }
      const practicePatientId = getPracticePatientId(useMainStore.getState());
      if (practicePatientId) {
        rememberWebAppChoice({
          practicePatientId,
          firstName: typed.firstName,
          lastName: typed.lastName,
          birthday: typed.birthday,
          lastActivityAt: new Date().toISOString(),
        });
      }
    }

    // Re-drawing the signature deliberately does not sign again: the PDF
    // already archived is the one the patient accepted by continuing.
    if (
      questionIsContract &&
      !completedWrites[signatureWriteKey(currentQuestion.id)]
    ) {
      const signed = await handleSignNormalContract(
        currentQuestion?.answer?.contractToSign,
      );
      if (!signed) {
        eventIndicator("error", t("ERROR_SAVING_ANSWERS"));
        return;
      }
      recordCompletedWrite(signatureWriteKey(currentQuestion.id));
    }

    if (questionIsDocumentUpload || questionIsRecipeUpload) {
      // Per asset, not per question: a file the patient adds after walking back
      // still has to be linked, while the ones already linked must not be again.
      const assets = (userAnswer?.documentUploadDetails ?? []).filter(
        (asset) => !completedWrites[assetWriteKey(asset.assetUrl)],
      );
      if (assets.length > 0) {
        const uploaded = await handleDocumentAndRecipeUpload(
          assets,
          questionIsRecipeUpload,
        );
        // Stay on the question so the patient can retry. Advancing would carry
        // them on with the documents never linked (PF-5637).
        if (!uploaded) {
          eventIndicator("error", t("ERROR_UPLOADING_DOCUMENTS"));
          return;
        }
        assets.forEach((asset) =>
          recordCompletedWrite(assetWriteKey(asset.assetUrl)),
        );
      }
    }

    if (isSkip === true) {
      if (currentQuestion) {
        setUserAnswerToQuestion(
          currentQuestion.id,
          undefined,
          currentQuestion.answer.answerId,
          true,
        );
      }
    }

    // do not proceed to next question if current question is cost bearer and steps for cost bearer info is not completed
    if (currentQuestionIsCostBearer && isSkip !== true) {
      if (currentCostBearerScreenStep === 0) {
        setCurrentCostBearerScreenStep(1);
        return;
      }

      if (currentCostBearerScreenStep === 1 && selectedCostBearerHasDocument) {
        setCurrentCostBearerScreenStep(2);
        return;
      }

      if (currentCostBearerScreenStep === 1) {
        saveCostBearerAnswer();
        setCurrentCostBearerScreenStep(0);
        await moveToNextQuestion();
        return;
      }
    }

    if (currentQuestionIsCostBearer && currentCostBearerScreenStep === 2) {
      if (!completedWrites[signatureWriteKey(currentQuestion.id)]) {
        const signed = await handleSignCostBearerContract(
          currentQuestion.answer.answerId,
          currentQuestion.id,
        );
        // Keep the step so the patient lands back on the signature, not on the
        // cost bearer selection, when they retry (PF-5637).
        if (!signed) {
          eventIndicator("error", t("ERROR_SAVING_ANSWERS"));
          return;
        }
        recordCompletedWrite(signatureWriteKey(currentQuestion.id));
      }
      setCurrentCostBearerScreenStep(0);
    }

    await moveToNextQuestion();
  };

  const handleNextQuestion = async (isSkip?: boolean) => {
    if (nextButtonDisabled && isSkip === false) return;
    // A ref, not state: a second tap arrives before React has re-rendered.
    if (isAdvancing.current) return;
    isAdvancing.current = true;
    setNextButtonLoading(true);
    try {
      await submitCurrentQuestion(isSkip);
    } finally {
      isAdvancing.current = false;
      setNextButtonLoading(false);
    }
  };

  const handleSkipQuestion = () => {
    handleNextQuestion(true);
  };

  const handlePreviousQuestion = async () => {
    if (currentQuestionIsCostBearer && currentCostBearerScreenStep > 0) {
      setCurrentCostBearerScreenStep(currentCostBearerScreenStep - 1);
      return;
    }
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      return;
    }
    // After a chooser the patient picks again without a new SMS; InAppLayout routes back to it.
    if (getWebAppChoices(useMainStore.getState())?.length) {
      leaveRecord();
      return;
    }
    // Read before the question: the session may end while it is open, and the logout clears the marker. A release
    // session (PF-6133) ends on its own end screen, never on the SMS login.
    const viaRelease = getIsReleaseSession(useMainStore.getState());
    // Leaving the form ends the verified session; signing in again needs a new code.
    const confirmed = await new Promise<boolean>((resolve) =>
      confirm({
        title: t("FORM_LEAVE_TITLE"),
        text: t("FORM_LEAVE_TEXT"),
        confirmText: t("FORM_LEAVE_CONFIRM"),
        cancelText: t("FORM_LEAVE_CANCEL"),
        resolve,
      }),
    );
    if (!confirmed) return;
    if (viaRelease) {
      await endReleaseSession({ ended: true });
      return;
    }
    await handleLogout();
    navigate("/welcome/login", { replace: true });
  };

  // From the run's second form on, the first question offers no way back: it would sign out a patient who has
  // already submitted forms in this run.
  const currentPracticeFormIndex = useMainStore(getCurrentPracticeFormIndex) ?? 0;
  const runStartFormIndex = useMainStore(getRunStartFormIndex);
  const hideBackButton =
    currentQuestionIndex === 0 &&
    currentPracticeFormIndex > runStartFormIndex &&
    !(currentQuestionIsCostBearer && currentCostBearerScreenStep > 0);

  return {
    handleNextQuestion,
    handleSkipQuestion,
    handlePreviousQuestion,
    hideBackButton,
  };
};
